import { useState, useCallback } from 'react'
import { pdf } from '@react-pdf/renderer'
import dayjs from 'dayjs'
import PdfReceipt, { fetchReceiptData } from './PdfReceipt'

// ---------------------------------------------------------------------------
// usePdfReceipt
//
// Encapsulates the full receipt generation lifecycle:
//   fetch data → render PDF → (open in new tab | hand back as a Blob)
//
// Why a hook?
// ───────────
// The steps above always travel together. Without a hook every callsite
// would have to duplicate loading/error state, the fetchReceiptData call, the
// pdf().toBlob() call, and the URL.createObjectURL dance. A hook collapses
// that into a single import and a single call.
//
// Design choices
// ──────────────
// • `supabase` is the only required argument. leaseId / periodFrom are passed
//   to the returned functions so the hook can be instantiated once (e.g. at
//   page level) and reused for multiple rows/buttons.
//
// • `buildReceiptBlob` is the low-level primitive: fetch + render, returned
//   as a Blob. It never opens anything itself, so it's equally useful for
//   "view the PDF" (generateReceipt, below) and "attach the PDF to an email"
//   (e.g. the quittance-sending flow in PaymentCard).
//
// • `generateReceipt` is `buildReceiptBlob` + "open in a new tab", kept as a
//   convenience for the existing "view receipt" callsites.
//
// • `loading` and `error` are exposed as separate flags rather than a single
//   status string so that callers can wire them to different UI slots
//   (e.g. a spinner on the button AND an Alert banner).
//
// • `clearError` is exposed so a caller can dismiss the error without needing
//   to know about internal state.
//
// • Neither function opens/returns a PDF when an error occurs, and loading is
//   always reset so the UI can never get stuck in a spinner.
//
// Usage
// ─────
//   const { generateReceipt, buildReceiptBlob, receiptLoading, receiptError, clearReceiptError }
//       = usePdfReceipt(supabase)
//
//   // View in a new tab:
//   await generateReceipt(leaseId, periodFrom)
//
//   // Get a Blob to attach elsewhere (e.g. to an email):
//   const result = await buildReceiptBlob(leaseId, periodFrom)
//   if (result) { const { blob, filename, data } = result; ... }
// ---------------------------------------------------------------------------
export function usePdfReceipt(supabase) {
    const [receiptLoading, setReceiptLoading] = useState(false)
    const [receiptError,   setReceiptError]   = useState(null)

    /**
     * Fetch receipt data and render it to a PDF Blob. Does not open or
     * download anything — the caller decides what to do with the result.
     *
     * @param {string} leaseId
     * @param {string} periodFrom  ISO date string (YYYY-MM-DD)
     * @returns {Promise<{ blob: Blob, filename: string, data: object } | null>}
     *   `null` on failure; `receiptError` is set as a side effect in that case.
     */
    const buildReceiptBlob = useCallback(
        async (leaseId, periodFrom) => {
            if (!leaseId || !periodFrom) return null

            try {
                // Step 1 — resolve all data from Supabase.
                // fetchReceiptData throws with a descriptive French message on
                // any fetch or validation failure; we let it propagate to catch.
                const data = await fetchReceiptData(supabase, leaseId, periodFrom)

                // Step 2 — render the PDF Document to a Blob.
                // PdfReceipt is a pure @react-pdf/renderer Document: no hooks,
                // no async inside. All data is already resolved in step 1.
                const blob = await pdf(
                    <PdfReceipt data={data} periodFrom={periodFrom} />
                ).toBlob()

                const filename = `quittance_${dayjs(periodFrom).format('YYYY-MM')}.pdf`

                return { blob, filename, data }
            } catch (err) {
                setReceiptError(
                    err.message ?? 'Erreur inattendue lors de la génération de la quittance.'
                )
                return null
            }
        },
        // supabase is the only external dependency; leaseId/periodFrom come in
        // as call-time arguments so they don't need to be in the dep array.
        [supabase]
    )

    /**
     * Fetch receipt data, render the PDF, and open it in a new browser tab.
     *
     * @param {string} leaseId
     * @param {string} periodFrom  ISO date string (YYYY-MM-DD)
     */
    const generateReceipt = useCallback(
        async (leaseId, periodFrom) => {
            if (!leaseId || !periodFrom) return

            setReceiptLoading(true)
            setReceiptError(null)

            const result = await buildReceiptBlob(leaseId, periodFrom)
            if (result) {
                const url = URL.createObjectURL(result.blob)
                window.open(url, '_blank')
            }

            setReceiptLoading(false)
        },
        [buildReceiptBlob]
    )

    const clearReceiptError = useCallback(() => setReceiptError(null), [])

    return { generateReceipt, buildReceiptBlob, receiptLoading, receiptError, clearReceiptError }
}